/**
 * 当地日期，形如 2024-05-20。
 * 按拍摄地的日历日期记，不随时区换算漂移：在东京晚上 11 点拍的照片，回到北京看也还是那一天。
 */
export type LocalDate = `${number}-${number}-${number}`;

const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

export function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

export function daysInYear(y: number): number {
  return isLeapYear(y) ? 366 : 365;
}

export function daysInMonth(y: number, m: number): number {
  return m === 2 ? (isLeapYear(y) ? 29 : 28) : [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1]!;
}

export function isLocalDate(s: unknown): s is LocalDate {
  if (typeof s !== 'string') return false;
  const m = LOCAL_DATE.exec(s);
  if (!m) return false;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  return y >= 1 && mo >= 1 && mo <= 12 && d >= 1 && d <= daysInMonth(y, mo);
}

export function parseLocalDate(s: LocalDate): { y: number; m: number; d: number } {
  const m = LOCAL_DATE.exec(s);
  if (!m) throw new RangeError(`不是有效的日期：${s}`);
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

export function formatLocalDate(y: number, m: number, d: number): LocalDate {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` as LocalDate;
}

/** 从 1970-01-01 起的天数。纯日历运算，与运行环境的时区无关。 */
export function toDayNumber(date: LocalDate): number {
  const { y, m, d } = parseLocalDate(date);
  return Math.round(Date.UTC(y, m - 1, d) / MS_PER_DAY);
}

export function fromDayNumber(n: number): LocalDate {
  const t = new Date(n * MS_PER_DAY);
  return formatLocalDate(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** b 比 a 晚几天（b 在前则为负） */
export function daysBetween(a: LocalDate, b: LocalDate): number {
  return toDayNumber(b) - toDayNumber(a);
}

export function addDays(date: LocalDate, n: number): LocalDate {
  return fromDayNumber(toDayNumber(date) + n);
}

/** 一年中的第几天，1 月 1 日是 1 */
export function dayOfYear(date: LocalDate): number {
  const { y } = parseLocalDate(date);
  return toDayNumber(date) - toDayNumber(formatLocalDate(y, 1, 1)) + 1;
}

/** 某个时刻在当前运行环境时区里的日期 */
export function localDateOf(t: Date | number): LocalDate {
  const d = typeof t === 'number' ? new Date(t) : t;
  return formatLocalDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}
